import { useState, useRef, useEffect } from 'react';
import { FiCalendar, FiChevronLeft, FiChevronRight, FiChevronDown } from 'react-icons/fi';
import './CustomDatePicker.css';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Max allowed date = March 31 of the END of the current financial year
// FY runs April 1 → March 31
function getFYMaxDate() {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth(); // 0-indexed; April = 3
  // April (3) or later → FY is y to y+1, ends March 31 next year
  // Jan-Mar (0-2)       → FY is (y-1) to y, ends March 31 this year
  return m >= 3 ? new Date(y + 1, 2, 31) : new Date(y, 2, 31);
}

const MIN_DATE = new Date(2019, 3, 1); // April 1, 2019

export default function CustomDatePicker({ value, onChange }) {
  const maxDate = getFYMaxDate();

  const parseDate = (val) => {
    if (!val) return null;
    const [y, m, d] = val.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  const selectedDate = parseDate(value);
  const today = new Date();

  const [isOpen, setIsOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() =>
    selectedDate ? selectedDate.getFullYear() : today.getFullYear()
  );
  const [viewMonth, setViewMonth] = useState(() =>
    selectedDate ? selectedDate.getMonth() : today.getMonth()
  );
  const [showMonthDd, setShowMonthDd] = useState(false);
  const [showYearDd, setShowYearDd] = useState(false);
  const [calStyle, setCalStyle] = useState({});

  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const onOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setIsOpen(false);
        setShowMonthDd(false);
        setShowYearDd(false);
      }
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  // Update view when value changes externally
  useEffect(() => {
    if (selectedDate) {
      setViewYear(selectedDate.getFullYear());
      setViewMonth(selectedDate.getMonth());
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCalendar = () => {
    if (!isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const calH = 350;
      const spaceBelow = window.innerHeight - rect.bottom;
      const top = spaceBelow >= calH + 8 ? rect.bottom + 6 : rect.top - calH - 6;
      setCalStyle({ top, left: rect.left, width: Math.max(rect.width, 300) });
    }
    setIsOpen((o) => !o);
    setShowMonthDd(false);
    setShowYearDd(false);
  };

  const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const getFirstDay = (y, m) => new Date(y, m, 1).getDay();

  const isAllowed = (y, m, d) => {
    const date = new Date(y, m, d);
    return date >= MIN_DATE && date <= maxDate;
  };

  const canGoPrev = () => {
    const pm = viewMonth === 0 ? 11 : viewMonth - 1;
    const py = viewMonth === 0 ? viewYear - 1 : viewYear;
    return new Date(py, pm, getDaysInMonth(py, pm)) >= MIN_DATE;
  };

  const canGoNext = () => {
    const nm = viewMonth === 11 ? 0 : viewMonth + 1;
    const ny = viewMonth === 11 ? viewYear + 1 : viewYear;
    return new Date(ny, nm, 1) <= maxDate;
  };

  const goPrev = () => {
    if (viewMonth === 0) { setViewYear((v) => v - 1); setViewMonth(11); }
    else setViewMonth((m) => m - 1);
  };

  const goNext = () => {
    if (viewMonth === 11) { setViewYear((v) => v + 1); setViewMonth(0); }
    else setViewMonth((m) => m + 1);
  };

  const selectDay = (day) => {
    if (!isAllowed(viewYear, viewMonth, day)) return;
    const m = String(viewMonth + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    onChange(`${viewYear}-${m}-${d}`);
    setIsOpen(false);
  };

  const formatDisplay = () => {
    if (!value) return 'Select date';
    const [y, m, d] = value.split('-').map(Number);
    return `${String(d).padStart(2, '0')} ${MONTHS[m - 1]} ${y}`;
  };

  // Build year list: MIN_DATE year → maxDate year
  const years = [];
  for (let y = MIN_DATE.getFullYear(); y <= maxDate.getFullYear(); y++) years.push(y);

  const daysCount = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDay(viewYear, viewMonth);

  return (
    <div className="cdp-wrap" ref={wrapRef}>
      {/* Trigger input */}
      <div
        ref={inputRef}
        className={`cdp-trigger ${isOpen ? 'cdp-trigger-open' : ''}`}
        onClick={openCalendar}
      >
        <FiCalendar size={14} className="cdp-trigger-icon" />
        <span className={`cdp-trigger-text ${!value ? 'cdp-trigger-placeholder' : ''}`}>
          {formatDisplay()}
        </span>
        <FiChevronDown
          size={13}
          className={`cdp-trigger-chevron ${isOpen ? 'cdp-trigger-chevron-up' : ''}`}
        />
      </div>

      {/* Floating calendar */}
      {isOpen && (
        <div className="cdp-calendar" style={{ ...calStyle, position: 'fixed' }}>
          {/* Header: dropdowns + nav */}
          <div className="cdp-cal-header">
            <div className="cdp-header-dropdowns">

              {/* Month dropdown */}
              <div className="cdp-dd">
                <button
                  type="button"
                  className={`cdp-dd-btn ${showMonthDd ? 'cdp-dd-btn-active' : ''}`}
                  onClick={(e) => { e.stopPropagation(); setShowMonthDd((s) => !s); setShowYearDd(false); }}
                >
                  {MONTHS[viewMonth]} <FiChevronDown size={11} />
                </button>
                {showMonthDd && (
                  <div className="cdp-dd-menu">
                    {MONTHS.map((mn, i) => (
                      <div
                        key={mn}
                        className={`cdp-dd-opt ${i === viewMonth ? 'cdp-dd-opt-active' : ''}`}
                        onClick={(e) => { e.stopPropagation(); setViewMonth(i); setShowMonthDd(false); }}
                      >
                        {mn}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Year dropdown */}
              <div className="cdp-dd">
                <button
                  type="button"
                  className={`cdp-dd-btn ${showYearDd ? 'cdp-dd-btn-active' : ''}`}
                  onClick={(e) => { e.stopPropagation(); setShowYearDd((s) => !s); setShowMonthDd(false); }}
                >
                  {viewYear} <FiChevronDown size={11} />
                </button>
                {showYearDd && (
                  <div className="cdp-dd-menu cdp-dd-menu-year">
                    {years.map((y) => (
                      <div
                        key={y}
                        className={`cdp-dd-opt ${y === viewYear ? 'cdp-dd-opt-active' : ''}`}
                        onClick={(e) => { e.stopPropagation(); setViewYear(y); setShowYearDd(false); }}
                      >
                        {y}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Prev / Next */}
            <div className="cdp-nav">
              <button type="button" className="cdp-nav-btn" onClick={goPrev} disabled={!canGoPrev()}>
                <FiChevronLeft size={15} />
              </button>
              <button type="button" className="cdp-nav-btn" onClick={goNext} disabled={!canGoNext()}>
                <FiChevronRight size={15} />
              </button>
            </div>
          </div>

          {/* Weekday labels */}
          <div className="cdp-weekdays">
            {DAYS.map((d) => (
              <div key={d} className="cdp-weekday">{d}</div>
            ))}
          </div>

          {/* Days grid */}
          <div className="cdp-days">
            {/* Empty cells for offset */}
            {Array.from({ length: firstDay }, (_, i) => (
              <div key={`gap-${i}`} className="cdp-day cdp-day-empty" />
            ))}

            {Array.from({ length: daysCount }, (_, i) => {
              const day = i + 1;
              const allowed = isAllowed(viewYear, viewMonth, day);
              const isSel =
                selectedDate &&
                selectedDate.getFullYear() === viewYear &&
                selectedDate.getMonth() === viewMonth &&
                selectedDate.getDate() === day;
              const isTod =
                today.getFullYear() === viewYear &&
                today.getMonth() === viewMonth &&
                today.getDate() === day;

              return (
                <div
                  key={day}
                  className={[
                    'cdp-day',
                    allowed ? 'cdp-day-allowed' : 'cdp-day-disabled',
                    isSel ? 'cdp-day-selected' : '',
                    isTod && !isSel ? 'cdp-day-today' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => selectDay(day)}
                >
                  {day}
                </div>
              );
            })}
          </div>

          {/* FY note */}
          <div className="cdp-fy-note">
            Dates allowed up to FY {maxDate.getFullYear() - 1}–{String(maxDate.getFullYear()).slice(2)}
          </div>
        </div>
      )}
    </div>
  );
}
